// Box levels: maps made of axis-aligned boxes, described as plain JSON. The built-in maps and the
// level builder both use this format, and levels built in the browser are saved in it.
// Source units and axes (X forward, Y left, Z up), like the rest of the simulation.

export type Vec3Tuple = [number, number, number];

export type { Shape } from './shapes';
import type { Shape } from './shapes';

/** What a box is in the game. */
export type BoxKind =
  /** Plain solid: platforms, walls, decoration. */
  | 'solid'
  /** Bhop block: drops after 10 ground ticks (respawn at the stage start). */
  | 'block'
  /** Start platform: the timer starts when you leave its zone. */
  | 'start'
  /** Stage checkpoint platform: its zone starts the next stage and sets the respawn point. */
  | 'checkpoint'
  /** End platform: the timer stops in its zone. */
  | 'end'
  /** Booster pad: jumping off it launches you (speed in `boost`). */
  | 'boost'
  /** Scenery only: drawn, never collides. */
  | 'deco'
  /** Launch pad: landing on it throws you (velocity in `boost`), no jump needed. */
  | 'pad'
  /** Ladder: climb it (look up and walk into it). Solid to the player. */
  | 'ladder'
  /** Water: swim in it (hold jump to swim up). Not solid. */
  | 'water'
  /** Wind: pushes you while you're in it (velocity in `boost`, like a CS:S trigger_push). Not solid. */
  | 'wind'
  /** Gravity field: inside it gravity is `gravity` times normal. Not solid. */
  | 'gravity';

/** Surface looks, drawn procedurally (no game assets). */
export const MATERIALS = [
  'grid',
  'concrete',
  'planks',
  'bricks',
  'tiles',
  'metal',
  'stone',
  'grass',
  'sand',
  'ice',
  'neon',
  'lava',
  'crystal',
  'glass',
  'water',
  'wind',
  'ladder',
] as const;
/** A drawn material, or an image texture from the asset manifest ("img:proto-dark-01"). */
export type MaterialName = (typeof MATERIALS)[number] | `img:${string}`;

/** Scenery model (render only), from the asset manifest. Position is the model's base. */
export interface LevelProp {
  model: string;
  pos: Vec3Tuple;
  yaw: number;
  scale: number;
}

export interface LevelBox {
  min: Vec3Tuple;
  max: Vec3Tuple;
  kind: BoxKind;
  material: MaterialName;
  /** Tint, "#rrggbb". */
  color: string;
  /** Boosters: launch velocity added on jump (Source units/s, Z up). */
  boost?: Vec3Tuple;
  /** Shape inside the bounding box (default box). */
  shape?: Shape;
  /** Quarter turns of the shape (triangles and ramps point different ways). */
  turn?: number;
  /** Moving piece: travels by `offset` and back every `period` seconds, starting at `phase` (0..1). */
  move?: { offset: Vec3Tuple; period: number; phase?: number };
  /** Spinning piece: degrees per second round its centre (positive: anticlockwise from above). */
  spin?: number;
  /** Blinking piece: there for `on` seconds, gone for `off`, starting `phase` (0..1) into the cycle. */
  blink?: { on: number; off: number; phase?: number };
  /** Gravity fields: gravity times this inside (0.1..3). */
  gravity?: number;
}

export interface LevelTheme {
  /** Sky gradient, top and horizon. */
  skyTop: string;
  skyHorizon: string;
  /** Fog colour and range. */
  fog: string;
  fogNear: number;
  fogFar: number;
  /** Light colour and strength (0..2). */
  sun: string;
  sunStrength: number;
  ambient: number;
  /** Floor far below (the void). */
  voidColor: string;
}

export interface LevelDef {
  /** Format version. */
  v: 1;
  /** Short id, used for personal bests ("bhop_meadow"). */
  name: string;
  title: string;
  author?: string;
  /** 1 (easy) .. 5 (hard). */
  difficulty: number;
  theme: LevelTheme;
  boxes: LevelBox[];
  spawn: { pos: Vec3Tuple; yaw: number };
  /** Scenery models (trees, rocks, plants...). */
  props?: LevelProp[];
  /** Falling below this height sends you back to the stage start. */
  failZ: number;
}

export const DEFAULT_THEME: LevelTheme = {
  skyTop: '#2b4a7a',
  skyHorizon: '#9cc3e6',
  fog: '#9cc3e6',
  fogNear: 2500,
  fogFar: 14000,
  sun: '#fff4e0',
  sunStrength: 1.1,
  ambient: 0.55,
  voidColor: '#1a2230',
};

/** How far up-and-down moving pieces travel: a gentle bob, so landing on them still feels solid. */
export const UPDOWN_TRAVEL = 24;

/** Height of stage zones above their platform: tall enough that you can't jump over them. */
export const ZONE_HEIGHT = 1600;
/** How far stage zones reach past their platform's sides. */
export const ZONE_MARGIN = 320;

/** Basic shape checks for levels from storage or a file. Returns an error message or null. */
export function validateLevel(v: unknown): string | null {
  if (!v || typeof v !== 'object') return 'Not a level file.';
  const d = v as Partial<LevelDef>;
  if (d.v !== 1) return 'Unknown level version.';
  if (typeof d.name !== 'string' || !d.name) return 'The level has no name.';
  if (!Array.isArray(d.boxes) || d.boxes.length === 0) return 'The level has no boxes.';
  if (d.boxes.length > 20000) return 'The level has too many boxes.';
  const isVec = (x: unknown) => Array.isArray(x) && x.length === 3 && x.every((n) => Number.isFinite(n));
  for (const b of d.boxes) {
    if (!b || !isVec(b.min) || !isVec(b.max)) return 'A box is broken.';
    if (b.min[0] >= b.max[0] || b.min[1] >= b.max[1] || b.min[2] >= b.max[2]) return 'A box has no size.';
  }
  if (!d.boxes.some((b) => b.kind === 'start')) return 'The level needs a start platform.';
  if (!d.boxes.some((b) => b.kind === 'end')) return 'The level needs an end platform.';
  if (!d.spawn || !isVec(d.spawn.pos)) return 'The level has no spawn point.';
  return null;
}
